import { useState } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import s from '../styles/RedesignRegisztracio.module.css'
import ErrorBoundary from '../src/local/ErrorBoundary'

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

const benefits = [
  'Push értesítők a lakóknak',
  'Facebook-integráció',
  'Városi séta modul',
  'Kedvező, havidíjas modell',
]

const infoSteps = [
  { num: '1', text: 'Regisztrációs kérelem kitöltése', done: true },
  { num: '2', text: 'Felvesszük Önnel a kapcsolatot 2 munkanapon belül' },
  { num: '3', text: 'Személyes bemutató és szerződéskötés' },
  { num: '4', text: 'Tartalom feltöltése és az app elindítása' },
]

const counties = ['Baranya','Bács-Kiskun','Békés','Borsod-Abaúj-Zemplén','Budapest','Csongrád-Csanád','Fejér','Győr-Moson-Sopron','Hajdú-Bihar','Heves','Jász-Nagykun-Szolnok','Komárom-Esztergom','Nógrád','Pest','Somogy','Szabolcs-Szatmár-Bereg','Tolna','Vas','Veszprém','Zala']

const positions = ['Polgármester','Alpolgármester','Jegyző','Aljegyző','IT / rendszergazda','Kommunikációs munkatárs','Egyéb']

const allModules = ['Hírek', 'Információk', 'Események', 'Bejelentések', 'Városi séta', 'Facebook-integráció']

interface FormData {
  municipality: string
  settlement: string
  county: string
  zip: string
  population: string
  municipalityEmail: string
  municipalityPhone: string
  website: string
  facebook: string
  lastName: string
  firstName: string
  position: string
  email: string
  phone: string
  note: string
  modules: string[]
  gdpr: boolean
}

const emptyForm: FormData = {
  municipality: '', settlement: '', county: '', zip: '', population: '',
  municipalityEmail: '', municipalityPhone: '',
  website: '', facebook: '',
  lastName: '', firstName: '', position: '', email: '', phone: '', note: '',
  modules: ['Hírek'],
  gdpr: false,
}

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''

async function waitForRecaptcha() {
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      clearInterval(interval)
      reject(new Error('reCAPTCHA betöltése sikertelen'))
    }, 10000)

    const interval = window.setInterval(() => {
      if (window.grecaptcha) {
        clearTimeout(timeout)
        clearInterval(interval)
        resolve()
      }
    }, 100)
  })
}

async function runRecaptchaAction(action: string) {
  if (!recaptchaSiteKey) {
    throw new Error('reCAPTCHA kulcs nincs beállítva')
  }

  if (!window.grecaptcha) {
    await waitForRecaptcha()
  }

  if (!window.grecaptcha) {
    throw new Error('reCAPTCHA nem érhető el')
  }

  return window.grecaptcha.execute(recaptchaSiteKey, { action })
}

export default function Regisztracio() {
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormData>(emptyForm)
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(prev => ({ ...prev, [key]: value }))
    setErrors([])
    setSubmitError('')
  }

  function toggleModule(mod: string) {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(mod)
        ? prev.modules.filter(m => m !== mod)
        : [...prev.modules, mod],
    }))
  }

  function validateStep(n: number): string[] {
    const errs: string[] = []
    if (n === 1) {
      if (!form.municipality.trim()) errs.push('Önkormányzat neve kötelező')
      if (!form.settlement.trim()) errs.push('Település neve kötelező')
      if (!form.county) errs.push('Vármegye kiválasztása kötelező')
      if (!form.zip.trim() || form.zip.trim().length < 4) errs.push('Irányítószám kötelező (4 számjegy)')
    }
    if (n === 2) {
      if (!form.lastName.trim()) errs.push('Vezetéknév kötelező')
      if (!form.firstName.trim()) errs.push('Keresztnév kötelező')
      if (!form.position) errs.push('Beosztás kiválasztása kötelező')
      if (!form.email.trim() || !form.email.includes('@')) errs.push('Érvényes e-mail cím kötelező')
      if (!form.phone.trim()) errs.push('Telefonszám kötelező')
    }
    if (n === 4) {
      if (!form.gdpr) errs.push('Az adatkezelési tájékoztató elfogadása kötelező')
    }
    return errs
  }

  function goNext() {
    const errs = validateStep(step)
    if (errs.length > 0) { setErrors(errs); return }
    setErrors([])
    setStep(step + 1)
  }

  async function handleSubmit() {
    const errs = validateStep(4)
    if (errs.length > 0) { setErrors(errs); return }

    if (!recaptchaSiteKey) {
      setSubmitError('reCAPTCHA kulcs nincs beállítva. Kérjük ellenőrizze a környezeti változókat.')
      return
    }

    setSubmitting(true)
    setSubmitError('')
    try {
      const recaptchaToken = await runRecaptchaAction('register')
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, recaptchaToken }),
      })
      const data = await res.json()
      if (!res.ok) {
        setSubmitError(data.error || 'Hiba történt a küldés során.')
      } else {
        setSubmitted(true)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Hiba történt a reCAPTCHA ellenőrzés során.'
      setSubmitError(message || 'Hálózati hiba. Kérjük próbálja újra.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ErrorBoundary>
      <div className={s.site}>
        <Head>
          <title>HolmiApp – Regisztráció</title>
          {recaptchaSiteKey && (
            <script
              src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
              async
              defer
            />
          )}
        </Head>

        <nav className={s.navBar}>
          <Link href="/" className={s.navLogo}>
            <span className={s.logoDot}><img src="/holmiapp2.svg" alt="" width={18} height={18} className={s.logoIcon} /></span>
            HolmiApp
          </Link>
          <Link href="/Onkormanyzat" className={s.backLink}>← Vissza az önkormányzati oldalra</Link>
        </nav>

        <div className={s.pageBody}>
          {/* LEFT INFO PANEL */}
          <div className={s.infoPanel}>
            <div className={s.eyebrowDark}><span className={s.eyebrowDot} />Csatlakozás</div>
            <h1 className={s.infoTitle}>Legyen része a <em>HolmiApp</em> hálózatnak</h1>
            <p className={s.infoSub}>Regisztráljon önkormányzatként, és nyisson közvetlen csatornát a lakók felé – push értesítőkkel, helyi hírekkel, eseményekkel.</p>
            <div className={s.benefitsList}>
              {benefits.map(b => (
                <div key={b} className={s.benefitItem}>
                  <svg className={s.benefitIcon} viewBox="0 0 24 24" fill="none" stroke="#6EEAB5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  <span>{b}</span>
                </div>
              ))}
            </div>
            <div className={s.stepsSection}>
              <div className={s.stepsTitle}>Hogyan zajlik a csatlakozás?</div>
              <div className={s.stepsList}>
                {infoSteps.map((st, i) => (
                  <div key={i} className={s.stepItem}>
                    <div className={`${s.stepNum} ${st.done ? s.stepNumDone : ''}`}>{st.done ? '✓' : st.num}</div>
                    <div className={s.stepText}>{st.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT FORM PANEL */}
          <div className={s.formPanel}>
            {submitted ? (
              <div className={s.success}>
                <div className={s.successIcon}>✓</div>
                <h2 className={s.successTitle}>Regisztráció elküldve!</h2>
                <p className={s.successSub}>Köszönjük! Kollégáink 2 munkanapon belül felveszik Önnel a kapcsolatot a megadott elérhetőségen.</p>
                <Link href="/" className={s.btnPrimary}>Vissza a főoldalra</Link>
              </div>
            ) : (
              <>
                {/* PROGRESS BAR */}
                <div className={s.progressBar}>
                  {['Önkormányzat', 'Kapcsolattartó', 'Modulok', 'Összefoglaló'].map((label, i) => (
                    <div key={label} className={`${s.progStep} ${i + 1 <= step ? s.progStepActive : ''} ${i + 1 < step ? s.progStepDone : ''}`}>
                      <div className={s.progNum}>{i + 1 < step ? '✓' : i + 1}</div>
                      <div className={s.progLabel}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* VALIDATION ERRORS */}
                {errors.length > 0 && (
                  <div className={s.errorBox}>
                    {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
                  </div>
                )}
                {submitError && <div className={s.errorBox}>⚠ {submitError}</div>}

                {/* STEP 1 */}
                {step === 1 && (
                  <div className={s.formSection}>
                    <h2 className={s.formTitle}>Az önkormányzat adatai</h2>
                    <p className={s.formSub}>Adja meg a település és az önkormányzat alapvető adatait.</p>
                    <div className={s.fieldGroup}><label>Önkormányzat neve *</label><input className={s.input} type="text" placeholder="Pl. Üllő Város Önkormányzat" value={form.municipality} onChange={e => set('municipality', e.target.value)} /></div>
                    <div className={s.fieldRow}>
                      <div className={s.fieldGroup}><label>Település neve *</label><input className={s.input} type="text" placeholder="Település" value={form.settlement} onChange={e => set('settlement', e.target.value)} /></div>
                      <div className={s.fieldGroup}><label>Vármegye *</label><select className={s.select} value={form.county} onChange={e => set('county', e.target.value)}><option value="">Válasszon...</option>{counties.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                    </div>
                    <div className={s.fieldRow}>
                      <div className={s.fieldGroup}><label>Irányítószám *</label><input className={s.input} type="text" placeholder="1234" maxLength={4} value={form.zip} onChange={e => set('zip', e.target.value.replace(/\D/g, ''))} /></div>
                      <div className={s.fieldGroup}><label>Lélekszám</label><select className={s.select} value={form.population} onChange={e => set('population', e.target.value)}><option value="">Válasszon...</option><option>500 alatt</option><option>500 – 2 000</option><option>2 000 – 5 000</option><option>5 000 – 20 000</option><option>20 000 – 50 000</option><option>50 000 – 100 000</option><option>100 000 felett</option></select></div>
                    </div>
                    <div className={s.fieldGroup}><label>Weboldal</label><input className={s.input} type="url" placeholder="https://" value={form.website} onChange={e => set('website', e.target.value)} /></div>
                    <div className={s.fieldGroup}><label>Facebook URL</label><input className={s.input} type="url" placeholder="https://facebook.com/..." value={form.facebook} onChange={e => set('facebook', e.target.value)} /></div>
                    <div className={s.btnRow}><button type="button" className={s.btnNext} onClick={goNext}>Tovább →</button></div>
                  </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <div className={s.formSection}>
                    <h2 className={s.formTitle}>Kapcsolattartó adatai</h2>
                    <p className={s.formSub}>Kinek küldhetjük az információkat a csatlakozásról?</p>
                    <div className={s.fieldRow}>
                      <div className={s.fieldGroup}><label>Vezetéknév *</label><input className={s.input} type="text" placeholder="Vezetéknév" value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
                      <div className={s.fieldGroup}><label>Keresztnév *</label><input className={s.input} type="text" placeholder="Keresztnév" value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
                    </div>
                    <div className={s.fieldGroup}><label>Beosztás *</label><select className={s.select} value={form.position} onChange={e => set('position', e.target.value)}><option value="">Válasszon...</option>{positions.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
                    <div className={s.fieldGroup}><label>E-mail *</label><input className={s.input} type="email" placeholder="onkormanyzat@telepules.hu" value={form.email} onChange={e => set('email', e.target.value)} /></div>
                    <div className={s.fieldGroup}><label>Telefonszám *</label><input className={s.input} type="tel" placeholder="+36 30 123 4567" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                    <div className={s.fieldGroup}><label>Egyéb megjegyzés</label><textarea className={s.textarea} placeholder="Ha van bármi kiegészítés..." value={form.note} onChange={e => set('note', e.target.value)} /></div>
                    <div className={s.btnRow}><button type="button" className={s.btnBack} onClick={() => setStep(1)}>← Vissza</button><button type="button" className={s.btnNext} onClick={goNext}>Tovább →</button></div>
                  </div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                  <div className={s.formSection}>
                    <h2 className={s.formTitle}>Igényelt modulok</h2>
                    <p className={s.formSub}>Válassza ki, melyik modulokat szeretné használni.</p>
                    <div className={s.moduleGrid}>
                      {allModules.map(m => (
                        <label key={m} className={s.moduleSelectCard}>
                          <input type="checkbox" checked={form.modules.includes(m)} onChange={() => toggleModule(m)} />
                          <span className={s.moduleCardName}>{m}</span>
                        </label>
                      ))}
                    </div>
                    <div className={s.btnRow}><button type="button" className={s.btnBack} onClick={() => setStep(2)}>← Vissza</button><button type="button" className={s.btnNext} onClick={goNext}>Tovább →</button></div>
                  </div>
                )}

                {/* STEP 4 — SUMMARY */}
                {step === 4 && (
                  <div className={s.formSection}>
                    <h2 className={s.formTitle}>Összefoglaló</h2>
                    <p className={s.formSub}>Ellenőrizze a megadott adatokat, majd küldje el a regisztrációt.</p>

                    <div className={s.summaryBlock}>
                      <div className={s.summaryTitle}>Önkormányzat</div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>Név</span><span className={s.summaryVal}>{form.municipality}</span></div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>Település</span><span className={s.summaryVal}>{form.settlement}, {form.county}</span></div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>Irányítószám</span><span className={s.summaryVal}>{form.zip}</span></div>
                      {form.population && <div className={s.summaryRow}><span className={s.summaryKey}>Lélekszám</span><span className={s.summaryVal}>{form.population}</span></div>}
                      {form.website && <div className={s.summaryRow}><span className={s.summaryKey}>Web</span><span className={s.summaryVal}>{form.website}</span></div>}
                    </div>

                    <div className={s.summaryBlock}>
                      <div className={s.summaryTitle}>Kapcsolattartó</div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>Név</span><span className={s.summaryVal}>{form.lastName} {form.firstName}</span></div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>Beosztás</span><span className={s.summaryVal}>{form.position}</span></div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>E-mail</span><span className={s.summaryVal}>{form.email}</span></div>
                      <div className={s.summaryRow}><span className={s.summaryKey}>Telefon</span><span className={s.summaryVal}>{form.phone}</span></div>
                      {form.note && <div className={s.summaryRow}><span className={s.summaryKey}>Megjegyzés</span><span className={s.summaryVal}>{form.note}</span></div>}
                    </div>

                    <div className={s.summaryBlock}>
                      <div className={s.summaryTitle}>Modulok</div>
                      <div className={s.summaryRow}><span className={s.summaryVal}>{form.modules.length > 0 ? form.modules.join(', ') : 'Nincs kiválasztva'}</span></div>
                    </div>

                    <div className={s.consentBox}>Az adatait bizalmasan kezeljük és kizárólag a kapcsolatfelvétel céljából használjuk fel.</div>
                    <label className={s.gdprLabel}>
                      <input type="checkbox" checked={form.gdpr} onChange={e => set('gdpr', e.target.checked)} />
                      Elfogadom az <a href="/Adatvedelem" target="_blank" rel="noopener noreferrer">adatkezelési tájékoztatót</a> *
                    </label>
                    <div className={s.btnRow}>
                      <button type="button" className={s.btnBack} onClick={() => setStep(3)}>← Vissza</button>
                      <button type="button" className={s.btnSubmit} onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Küldés...' : 'Regisztráció elküldése'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
}
